// src/components/dashboard/BackupDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Save,
  RefreshCw,
  Database,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Upload,
  Trash2,
  HardDrive,
  Eye,
  Download,
  Settings,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Activity,
  Shield,
  Calendar,
  FileText,
  Users,
  Zap,
  Plus,
  Filter,
  Search,
  MoreVertical,
  Info
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useBackup } from '../../context/BackupContext';
import backupService from '../../services/BackupService';
import {
  formatFileSize,
  formatDuration,
  formatRelativeTime,
  formatBackupDate,
  calculateBackupStats,
  sortBackups,
  filterBackups
} from '../../services/backupUtils';
import { BACKUP_CONFIG } from '../../services/backupConfig';

const BackupDashboard = () => {
  const { user } = useAuth();
  const backup = useBackup();
  
  // États du dashboard
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [storageInfo, setStorageInfo] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  
  // États de l'interface
  const [selectedBackups, setSelectedBackups] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid ou list
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Filtres
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });

  // Options pour nouvelle sauvegarde
  const [backupOptions, setBackupOptions] = useState({
    type: 'manual',
    description: '',
    includeCritical: true,
    includeSystem: true,
    includeOptional: false,
    collections: []
  });

  // Charger les données au montage
  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000); // Refresh toutes les 30s
    return () => clearInterval(interval);
  }, []);

  // Recharger quand les filtres changent
  useEffect(() => {
    if (backups.length > 0) {
      applyFiltersAndSort();
    }
  }, [filters, sortBy, sortOrder]);

  /**
   * Charger toutes les données du dashboard
   */
  const loadDashboardData = async () => {
    try {
      setError(null);
      await Promise.all([
        loadBackups(),
        loadStorageInfo(),
        loadPerformanceData()
      ]);
    } catch (err) {
      console.error('Erreur chargement dashboard:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Charger la liste des sauvegardes
   */
  const loadBackups = async () => {
    try {
      const backupsList = await backupService.listBackups({ limit: 100 });
      setBackups(backupsList);
      
      const backupStats = calculateBackupStats(backupsList);
      setStats(backupStats);
      
      console.log(`📋 ${backupsList.length} sauvegardes chargées`);
    } catch (err) {
      console.error('Erreur chargement backups:', err);
      throw err;
    }
  };

  /**
   * Charger les informations de stockage
   */
  const loadStorageInfo = async () => {
    try {
      const storage = await backupService.getStorageUsage();
      setStorageInfo(storage);
    } catch (err) {
      console.error('Erreur chargement stockage:', err);
      // Ne pas bloquer si le stockage n'est pas accessible
    }
  };

  /**
   * Charger les données de performance
   */
  const loadPerformanceData = async () => {
    try {
      const perf = await backupService.analyzePerformance();
      setPerformance(perf);
    } catch (err) {
      console.error('Erreur chargement performance:', err);
      // Ne pas bloquer si les performances ne sont pas calculables
    }
  };

  /**
   * Appliquer les filtres et le tri
   */
  const applyFiltersAndSort = () => {
    let filtered = filterBackups(backups, {
      status: filters.status || undefined,
      type: filters.type || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined
    });

    // Filtrage par recherche textuelle
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(backup => 
        backup.id.toLowerCase().includes(searchLower) ||
        backup.name?.toLowerCase().includes(searchLower) ||
        backup.description?.toLowerCase().includes(searchLower)
      );
    }

    // Tri
    const sorted = sortBackups(filtered, sortBy, sortOrder);
    
    return sorted;
  };

  /**
   * Créer une nouvelle sauvegarde
   */
  const handleCreateBackup = async () => {
    if (!user || !BACKUP_CONFIG.SECURITY.PERMISSIONS.CREATE_BACKUP.includes(user.role)) {
      alert('Permissions insuffisantes pour créer une sauvegarde');
      return;
    }

    setActionLoading('create');
    try {
      const result = await backupService.createFullBackup({
        userId: user.uid,
        type: backupOptions.type,
        description: backupOptions.description,
        includeCritical: backupOptions.includeCritical,
        includeSystem: backupOptions.includeSystem,
        includeOptional: backupOptions.includeOptional,
        collections: backupOptions.collections.length > 0 ? backupOptions.collections : null
      });

      if (result.success) {
        alert(`✅ ${result.message}`);
        await loadBackups();
        setShowCreateModal(false);
        
        // Reset des options
        setBackupOptions({
          type: 'manual',
          description: '',
          includeCritical: true,
          includeSystem: true,
          includeOptional: false,
          collections: []
        });
      }
    } catch (err) {
      console.error('Erreur création backup:', err);
      alert(`❌ Erreur: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Supprimer une sauvegarde
   */
  const handleDeleteBackup = async (backupId) => {
    if (!user || !BACKUP_CONFIG.SECURITY.PERMISSIONS.DELETE_BACKUP.includes(user.role)) {
      alert('Permissions insuffisantes pour supprimer une sauvegarde');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette sauvegarde ?')) {
      return;
    }

    setActionLoading(`delete-${backupId}`);
    try {
      await backupService.deleteBackup(backupId);
      alert('✅ Sauvegarde supprimée avec succès');
      await loadBackups();
    } catch (err) {
      console.error('Erreur suppression backup:', err);
      alert(`❌ Erreur: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  /**
// Ajoute après la fonction handleDeleteBackup
/**
 * Voir les détails d'une sauvegarde
 */
const handleViewDetails = (backup) => {
  // Option 1: Modal simple avec les détails
  const details = `
🔍 DÉTAILS DE LA SAUVEGARDE

ID: ${backup.id}
Nom: ${backup.name || 'Sans nom'}
Type: ${backup.type}
Statut: ${backup.status}
Créée le: ${formatBackupDate(backup.timestamp)}
Créée par: ${backup.createdBy}

📊 MÉTADONNÉES:
- Documents: ${backup.metadata?.totalDocuments || 0}
- Taille: ${formatFileSize(backup.metadata?.totalSize || 0)}
- Durée: ${formatDuration(backup.metadata?.duration || 0)}
- Collections: ${backup.collectionsCompleted?.length || 0}

📁 COLLECTIONS SAUVEGARDÉES:
${backup.collectionsCompleted?.join(', ') || 'Non spécifiées'}

${backup.description ? `📝 DESCRIPTION:\n${backup.description}` : ''}
  `;
  
  alert(details);
  
  // Option 2: Tu peux aussi ouvrir un modal plus sophistiqué
  // setSelectedBackupForDetails(backup);
  // setShowDetailsModal(true);
};

/**
 * Restaurer une sauvegarde
 */
const handleRestoreBackup = async (backup) => {
  if (!user || !BACKUP_CONFIG.SECURITY.PERMISSIONS.RESTORE_BACKUP.includes(user.role)) {
    alert('Permissions insuffisantes pour restaurer une sauvegarde');
    return;
  }

  // Vérifications de sécurité
  if (backup.status !== 'completed') {
    alert('Impossible de restaurer : cette sauvegarde n\'est pas terminée');
    return;
  }

  const confirmMessage = `
⚠️ ATTENTION - RESTAURATION DE DONNÉES

Vous allez restaurer la sauvegarde :
- ID: ${backup.id}
- Créée: ${formatRelativeTime(backup.timestamp)}
- Documents: ${backup.metadata?.totalDocuments || 0}
- Taille: ${formatFileSize(backup.metadata?.totalSize || 0)}

🚨 CETTE ACTION VA MODIFIER VOS DONNÉES ACTUELLES !

Voulez-vous vraiment continuer ?
  `;

  if (!window.confirm(confirmMessage)) {
    return;
  }

  setActionLoading(`restore-${backup.id}`);
  try {
    const result = await backupService.restoreFromBackup(backup.id, {
      userId: user.uid,
      mode: 'merge', // Mode fusion par défaut
      createBackupBefore: true // Créer une sauvegarde de sécurité
    });

    alert(`✅ Restauration terminée avec succès !\n\n${result.message}\n\nDocuments restaurés: ${result.documentsRestored}`);
    
    // Recharger les données
    await loadDashboardData();
    
  } catch (err) {
    console.error('Erreur restauration:', err);
    alert(`❌ Erreur lors de la restauration: ${err.message}`);
  } finally {
    setActionLoading(null);
  }
};
/**

   * Supprimer plusieurs sauvegardes
   */
  const handleDeleteSelected = async () => {
    if (selectedBackups.length === 0) return;

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedBackups.length} sauvegarde(s) ?`)) {
      return;
    }

    setActionLoading('delete-multiple');
    try {
      await Promise.all(
        selectedBackups.map(id => backupService.deleteBackup(id))
      );
      alert(`✅ ${selectedBackups.length} sauvegarde(s) supprimée(s)`);
      setSelectedBackups([]);
      await loadBackups();
    } catch (err) {
      console.error('Erreur suppression multiple:', err);
      alert(`❌ Erreur lors de la suppression`);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Télécharger une sauvegarde
   */
  const handleDownloadBackup = async (backup) => {
    try {
      if (backup.downloadURL) {
        const link = document.createElement('a');
        link.href = backup.downloadURL;
        link.download = `${backup.name || backup.id}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('URL de téléchargement non disponible');
      }
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      alert('Erreur lors du téléchargement');
    }
  };

  /**
   * Gérer la sélection des sauvegardes
   */
  const handleBackupSelection = (backupId, isSelected) => {
    if (isSelected) {
      setSelectedBackups(prev => [...prev, backupId]);
    } else {
      setSelectedBackups(prev => prev.filter(id => id !== backupId));
    }
  };

  /**
   * Sélectionner toutes les sauvegardes visibles
   */
  const handleSelectAll = () => {
    const visibleBackups = applyFiltersAndSort();
    if (selectedBackups.length === visibleBackups.length) {
      setSelectedBackups([]);
    } else {
      setSelectedBackups(visibleBackups.map(b => b.id));
    }
  };

  /**
   * Obtenir la couleur selon le statut
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100 border-green-200';
      case 'failed': return 'text-red-600 bg-red-100 border-red-200';
      case 'in_progress': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'corrupted': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  /**
   * Obtenir l'icône selon le statut
   */
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'in_progress': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'corrupted': return <AlertTriangle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  /**
   * Obtenir l'icône selon le type
   */
  const getTypeIcon = (type) => {
    switch (type) {
      case 'manual': return <Users className="w-4 h-4" />;
      case 'auto': return <Clock className="w-4 h-4" />;
      case 'scheduled': return <Calendar className="w-4 h-4" />;
      case 'emergency': return <AlertTriangle className="w-4 h-4" />;
      case 'test': return <Activity className="w-4 h-4" />;
      default: return <Database className="w-4 h-4" />;
    }
  };

  // Appliquer les filtres aux données affichées
  const displayedBackups = applyFiltersAndSort();

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-2 text-lg">Chargement du dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Sauvegardes</h1>
          <p className="text-gray-600 mt-1">Gestion et monitoring du système de sauvegarde</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadDashboardData}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={actionLoading === 'create'}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            {actionLoading === 'create' ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Nouvelle Sauvegarde
          </button>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Cartes de statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total des sauvegardes */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sauvegardes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.lastBackup ? formatRelativeTime(stats.lastBackup.timestamp) : 'Aucune'}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Database className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Taux de réussite */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taux de Réussite</p>
                <p className="text-2xl font-bold text-gray-900">{stats.successRate.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.successful}/{stats.total} réussies
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                {stats.successRate >= 90 ? (
                  <TrendingUp className="w-6 h-6 text-green-600" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-yellow-600" />
                )}
              </div>
            </div>
          </div>

          {/* Taille totale */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taille Totale</p>
                <p className="text-2xl font-bold text-gray-900">{formatFileSize(stats.totalSize)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Moy: {formatFileSize(stats.averageSize)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <HardDrive className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Performance */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Durée Moyenne</p>
                <p className="text-2xl font-bold text-gray-900">{formatDuration(stats.averageDuration)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {performance ? `${(performance.throughput?.documentsPerSecond || 0).toFixed(0)} docs/s` : ''}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informations de stockage */}
      {storageInfo && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Utilisation du Stockage</h3>
            <HardDrive className="w-5 h-5 text-gray-500" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{formatFileSize(storageInfo.totalSize)}</p>
              <p className="text-sm text-gray-600">Taille totale</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{storageInfo.fileCount}</p>
              <p className="text-sm text-gray-600">Fichiers</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{formatFileSize(storageInfo.averageFileSize)}</p>
              <p className="text-sm text-gray-600">Taille moyenne</p>
            </div>
          </div>
        </div>
      )}

      {/* Barre d'outils */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Contrôles de gauche */}
          <div className="flex items-center space-x-4">
            {/* Sélection multiple */}
            {selectedBackups.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedBackups.length} sélectionnée(s)
                </span>
                <button
                  onClick={handleDeleteSelected}
                  disabled={actionLoading === 'delete-multiple'}
                  className="flex items-center px-3 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded text-sm transition-colors"
                >
                  {actionLoading === 'delete-multiple' ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Supprimer
                </button>
              </div>
            )}

            {/* Sélectionner tout */}
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {selectedBackups.length === displayedBackups.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          </div>

          {/* Contrôles de droite */}
          <div className="flex items-center space-x-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtres */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-3 py-2 rounded-lg transition-colors ${
                showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtres
            </button>

            {/* Mode d'affichage */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              >
                <BarChart3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
              >
                <FileText className="w-4 h-4" />
              </button>
            </div>

            {/* Tri */}
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="timestamp-desc">Plus récent</option>
              <option value="timestamp-asc">Plus ancien</option>
              <option value="size-desc">Plus volumineux</option>
              <option value="size-asc">Plus léger</option>
              <option value="duration-desc">Plus long</option>
              <option value="duration-asc">Plus rapide</option>
            </select>
          </div>
        </div>

        {/* Panneau de filtres */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous</option>
                  <option value="completed">Terminée</option>
                  <option value="failed">Échouée</option>
                  <option value="in_progress">En cours</option>
                  <option value="corrupted">Corrompue</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous</option>
                  <option value="manual">Manuelle</option>
                  <option value="auto">Automatique</option>
                  <option value="scheduled">Programmée</option>
                  <option value="emergency">Urgence</option>
                  <option value="test">Test</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date à</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setFilters({ status: '', type: '', dateFrom: '', dateTo: '', search: '' })}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Réinitialiser les filtres
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Liste des sauvegardes */}
      <div className="bg-white border border-gray-200 rounded-lg">
        {displayedBackups.length === 0 ? (
          <div className="text-center py-12">
            <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {backups.length === 0 ? 'Aucune sauvegarde trouvée' : 'Aucune sauvegarde ne correspond aux filtres'}
            </p>
            {backups.length === 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer la première sauvegarde
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          /* Vue grille */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
            {displayedBackups.map(backup => (
              <div key={backup.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedBackups.includes(backup.id)}
                      onChange={(e) => handleBackupSelection(backup.id, e.target.checked)}
                      className="mr-3"
                    />
                    <div className="flex items-center">
                      {getTypeIcon(backup.type)}
                      <span className="ml-2 font-medium text-gray-900 truncate">
                        {backup.name || backup.id}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(backup.status)}`}>
                      {getStatusIcon(backup.status)}
                      <span className="ml-1">{backup.status}</span>
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Créée:</span>
                    <span>{formatRelativeTime(backup.timestamp)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Taille:</span>
                    <span>{formatFileSize(backup.metadata?.totalSize || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Documents:</span>
                    <span>{(backup.metadata?.totalDocuments || 0).toLocaleString()}</span>
                  </div>
                  {backup.metadata?.duration && (
                    <div className="flex justify-between">
                      <span>Durée:</span>
                      <span>{formatDuration(backup.metadata.duration)}</span>
                    </div>
                  )}
                </div>

                {backup.description && (
                  <p className="text-xs text-gray-500 mt-3 truncate" title={backup.description}>
                    {backup.description}
                  </p>
                )}

                <div className="flex justify-end space-x-2 mt-4">
                  {backup.status === 'completed' && (
                    <>
                      <button
                        onClick={() => handleDownloadBackup(backup)}
                        className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => console.log('Restaurer:', backup.id)}
                        className="p-2 text-gray-500 hover:text-green-600 transition-colors"
                        title="Restaurer"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => console.log('Détails:', backup.id)}
                    className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                    title="Voir détails"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteBackup(backup.id)}
                    disabled={actionLoading === `delete-${backup.id}`}
                    className="p-2 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
                    title="Supprimer"
                  >
                    {actionLoading === `delete-${backup.id}` ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Vue liste */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedBackups.length === displayedBackups.length && displayedBackups.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sauvegarde
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taille
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documents
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Créée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedBackups.map(backup => (
                  <tr key={backup.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedBackups.includes(backup.id)}
                        onChange={(e) => handleBackupSelection(backup.id, e.target.checked)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {backup.name || backup.id}
                        </div>
                        {backup.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs" title={backup.description}>
                            {backup.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(backup.status)}`}>
                        {getStatusIcon(backup.status)}
                        <span className="ml-1">{backup.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        {getTypeIcon(backup.type)}
                        <span className="ml-2">{backup.type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatFileSize(backup.metadata?.totalSize || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(backup.metadata?.totalDocuments || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div>{formatRelativeTime(backup.timestamp)}</div>
                        {backup.metadata?.duration && (
                          <div className="text-xs text-gray-400">
                            Durée: {formatDuration(backup.metadata.duration)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {backup.status === 'completed' && (
                          <>
                            <button
                              onClick={() => handleDownloadBackup(backup)}
                              className="text-gray-500 hover:text-blue-600 transition-colors"
                              title="Télécharger"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => console.log('Restaurer:', backup.id)}
                              className="text-gray-500 hover:text-green-600 transition-colors"
                              title="Restaurer"
                            >
                              <Upload className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => console.log('Détails:', backup.id)}
                          className="text-gray-500 hover:text-blue-600 transition-colors"
                          title="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBackup(backup.id)}
                          disabled={actionLoading === `delete-${backup.id}`}
                          className="text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
                          title="Supprimer"
                        >
                          {actionLoading === `delete-${backup.id}` ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de création de sauvegarde */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Nouvelle Sauvegarde</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Type de sauvegarde */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de sauvegarde</label>
                <select
                  value={backupOptions.type}
                  onChange={(e) => setBackupOptions(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="manual">Manuelle</option>
                  <option value="emergency">Urgence</option>
                  <option value="test">Test</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (optionnelle)</label>
                <textarea
                  value={backupOptions.description}
                  onChange={(e) => setBackupOptions(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Décrivez le contexte de cette sauvegarde..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Collections à inclure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Collections à sauvegarder</label>
                <div className="space-y-3">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={backupOptions.includeCritical}
                      onChange={(e) => setBackupOptions(prev => ({ ...prev, includeCritical: e.target.checked }))}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Collections critiques</span>
                      <p className="text-sm text-gray-600">
                        Données client essentielles ({BACKUP_CONFIG.CRITICAL_COLLECTIONS.join(', ')})
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={backupOptions.includeSystem}
                      onChange={(e) => setBackupOptions(prev => ({ ...prev, includeSystem: e.target.checked }))}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Collections système</span>
                      <p className="text-sm text-gray-600">
                        Configuration et paramètres ({BACKUP_CONFIG.SYSTEM_COLLECTIONS.join(', ')})
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={backupOptions.includeOptional}
                      onChange={(e) => setBackupOptions(prev => ({ ...prev, includeOptional: e.target.checked }))}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Collections optionnelles</span>
                      <p className="text-sm text-gray-600">
                        Analytics et cache ({BACKUP_CONFIG.OPTIONAL_COLLECTIONS.join(', ')})
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Résumé */}
              {(backupOptions.includeCritical || backupOptions.includeSystem || backupOptions.includeOptional) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex">
                    <Info className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800">Résumé de la sauvegarde</p>
                      <p className="text-blue-700 mt-1">
                        Cette sauvegarde incluera{' '}
                        {[
                          backupOptions.includeCritical && `${BACKUP_CONFIG.CRITICAL_COLLECTIONS.length} collections critiques`,
                          backupOptions.includeSystem && `${BACKUP_CONFIG.SYSTEM_COLLECTIONS.length} collections système`,
                          backupOptions.includeOptional && `${BACKUP_CONFIG.OPTIONAL_COLLECTIONS.length} collections optionnelles`
                        ].filter(Boolean).join(', ')}.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={actionLoading === 'create'}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateBackup}
                disabled={actionLoading === 'create' || (!backupOptions.includeCritical && !backupOptions.includeSystem && !backupOptions.includeOptional)}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === 'create' ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Créer la Sauvegarde
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupDashboard;